import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import WelcomeScreen from '../components/WelcomeScreen';
import MaintenanceScreen from '../components/MaintenanceScreen';
import api from '../api/axios';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const notification = useNotification();
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [userName, setUserName] = useState('Admin Demo');
  const [userPhoto, setUserPhoto] = useState('');
  const errorShownRef = useRef(false); // Prevent duplicate errors

  const handleTelegramLogin = useCallback(async (initData) => {
    if (errorShownRef.current) return; // Prevent duplicate calls
    
    console.log('Handling Telegram login...');
    setLoading(true);
    try {
      const response = await api.post('/auth/telegram', { initData });
      console.log('Telegram login response:', response.data);
      
      if (response.data.success) {
        setUserName(response.data.user.username || response.data.user.first_name || 'User');
        setUserPhoto(response.data.user.profile_photo_url || '');
        setShowWelcome(true);
        setTimeout(() => {
          login(response.data.token, response.data.user);
        }, 3000);
      }
    } catch (err) {
      console.error('Telegram login failed:', err);
      setLoading(false);
      
      if (errorShownRef.current) return; // Prevent duplicate errors
      errorShownRef.current = true;
      
      // Check if maintenance mode
      if (err.response?.status === 503 && err.response?.data?.maintenance) {
        console.log('Maintenance mode active, showing maintenance screen');
        setShowMaintenance(true);
        setMaintenanceMessage(err.response.data.message);
      }
      // Check if banned
      else if (err.response?.status === 403 && err.response?.data?.banned) {
        notification.error(err.response.data.error, 5000);
      }
      // Other errors
      else {
        const errorMsg = err.response?.data?.error || 'Login failed. Please try again.';
        notification.error(errorMsg, 5000);
      }
    }
  }, [login, notification]);

  const handleBrowserLogin = useCallback(async (hash, userId, timestamp) => {
    if (errorShownRef.current) return; // Prevent duplicate calls
    
    console.log('Handling browser login...');
    setLoading(true);
    try {
      const response = await api.post('/auth/browser', {
        hash,
        userId,
        timestamp: parseInt(timestamp)
      });
      
      console.log('Browser login response:', response.data);
      
      if (response.data.success) {
        setUserName(response.data.user.username || response.data.user.first_name || 'User');
        setUserPhoto(response.data.user.profile_photo_url || '');
        setShowWelcome(true);
        setTimeout(() => {
          login(response.data.token, response.data.user);
        }, 3000);
      }
    } catch (err) {
      console.error('Browser login failed:', err);
      setLoading(false);
      
      if (errorShownRef.current) return; // Prevent duplicate errors
      errorShownRef.current = true;
      
      // Check if maintenance mode
      if (err.response?.status === 503 && err.response?.data?.maintenance) {
        console.log('Maintenance mode active, showing maintenance screen');
        setShowMaintenance(true);
        setMaintenanceMessage(err.response.data.message);
      }
      // Check if banned
      else if (err.response?.status === 403 && err.response?.data?.banned) {
        notification.error(err.response.data.error, 5000);
      }
      // Other errors
      else {
        const errorMsg = err.response?.data?.error || 'Login failed. Please try again.';
        notification.error(errorMsg, 5000);
      }
    }
  }, [login, notification]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (errorShownRef.current) return; // Don't retry if error already shown
    
    const hash = searchParams.get('hash');
    const userId = searchParams.get('userId');
    const timestamp = searchParams.get('timestamp');

    if (hash && userId && timestamp) {
      handleBrowserLogin(hash, userId, timestamp);
      return;
    }

    const checkTelegramWebApp = () => {
      const tg = window.Telegram?.WebApp;
      if (tg && tg.initData) {
        console.log('Telegram WebApp detected, attempting login...');
        tg.ready();
        tg.expand();
        handleTelegramLogin(tg.initData);
      }
    };

    checkTelegramWebApp();
    const timer = setTimeout(checkTelegramWebApp, 500);
    return () => clearTimeout(timer);
  }, [searchParams, handleBrowserLogin, handleTelegramLogin]);

  if (showWelcome) {
    return <WelcomeScreen username={userName} profilePhoto={userPhoto} onComplete={() => navigate('/', { replace: true })} />;
  }

  if (showMaintenance) {
    return <MaintenanceScreen customMessage={maintenanceMessage} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1, rotate: 360 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="inline-block mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
              <Sparkles className="relative text-cyan-400" size={64} />
            </div>
          </motion.div>

          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent">
              CyborX
            </span>
          </h1>
          <p className="text-gray-400 text-lg">Secure Telegram-Integrated WebApp</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-md rounded-xl p-8 border border-cyan-500/20"
        >
          <div className="text-center py-8">
            <p className="text-gray-400 mb-6">
              Please open this app through the Telegram bot to authenticate.
            </p>
            <a
              href="https://t.me/CyborXxRobot"
              className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold rounded-lg transition-all mb-4"
            >
              Open Telegram Bot
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 text-center"
        >
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-cyan-400">🔒</p>
              <p className="text-gray-400 text-sm mt-1">Secure</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-400">⚡</p>
              <p className="text-gray-400 text-sm mt-1">Fast</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-400">🚀</p>
              <p className="text-gray-400 text-sm mt-1">Reliable</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Login;
